import axios from 'axios';
import { fetchSessionToken } from './appBridge';

const API_BASE_URL = process.env.REACT_BACKEND_BASE_URL || 'https://shopify.dropkid.de';
console.log('API_BASE_URL', API_BASE_URL);
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach a fresh Shopify session token to every request so the backend can
// verify the caller is an authenticated admin. Public storefront endpoints
// simply ignore it.
api.interceptors.request.use(async (config) => {
  const token = await fetchSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Products API
export const productsAPI = {
  getAll: (params = {}) => api.get('/api/products', { params }),
  getById: (id) => api.get(`/api/products/${id}`),
  create: (data) => api.post('/api/products', data),
  createBatch: (products) => api.post('/api/products/batch', { products }),
  update: (id, data) => api.put(`/api/products/${id}`, data),
  deactivate: (id) => api.patch(`/api/products/${id}/deactivate`),
  activate: (id) => api.patch(`/api/products/${id}/activate`),
  delete: (id) => api.delete(`/api/products/${id}`), // Keep for backward compatibility
  getCategories: (params = {}) => api.get('/api/products/categories/list', { params }),
  syncFromShopify: (shopifyProductId, data) => api.post(`/api/products/sync/${shopifyProductId}`, data),
  getConditions: (id) => api.get(`/api/products/${id}/conditions`),
  getCondition: (id, conditionId) => api.get(`/api/products/${id}/conditions/${conditionId}`),
  getConditionHistory: (id, conditionId) => api.get(`/api/products/${id}/conditions/${conditionId}/history`),
  getConditionDashboard: () => api.get('/api/products/conditions/dashboard'),
  saveConditions: (id, conditions) => api.put(`/api/products/${id}/conditions`, { conditions }),
  createCondition: (id, data) => api.post(`/api/products/${id}/conditions`, data),
  updateCondition: (id, conditionId, data) => api.patch(`/api/products/${id}/conditions/${conditionId}`, data),
  deleteCondition: (id, conditionId) => api.delete(`/api/products/${id}/conditions/${conditionId}`),
  getBarcodes: (id) => api.get(`/api/products/${id}/barcodes`),
  getByBarcode: (barcode) => api.get(`/api/products/items/by-barcode/${encodeURIComponent(barcode)}`),
  getItemBarcode: (id, conditionId) => api.get(`/api/products/${id}/conditions/${conditionId}/barcode`),
  updateItemBarcode: (id, conditionId, barcode) => api.patch(
    `/api/products/${id}/conditions/${conditionId}/barcode`,
    { barcode }
  ),
  assignItemBarcode: (id, conditionId, barcode, overwrite = false) => api.put(
    `/api/products/${id}/conditions/${conditionId}/barcode`,
    { barcode, overwrite }
  ),
};

// Rentals API
export const rentalsAPI = {
  getAll: (params = {}) => api.get('/api/rentals', { params }),
  getById: (id) => api.get(`/api/rentals/${id}`),
  create: (data) => api.post('/api/rentals', data),
  updateStatus: (id, data) => api.patch(`/api/rentals/${id}/status`, data),
  getOverdue: () => api.get('/api/rentals/status/overdue'),
  getStats: () => api.get('/api/rentals/stats/summary'),
  getByProduct: (shopifyProductId, params = {}) => api.get(`/api/rentals/by-product/${shopifyProductId}`, { params }),
  getCurrent: (params = {}) => api.get('/api/rentals/current', { params }),
  getPacking: (id) => api.get(`/api/rentals/${id}/packing`),
  savePacking: (id, assignments) => api.put(`/api/rentals/${id}/packing`, { assignments }),
};

// Availability API
export const availabilityAPI = {
  check: (data) => api.post('/api/availability/check', data),
  getCalendar: (productId, days = 180) => api.get(`/api/availability/calendar/${productId}?days=${days}`),
};

// Add-Ons API
export const addOnsAPI = {
  getAll: (params = {}) => api.get('/api/addons', { params }),
  getActive: () => api.get('/api/addons/active'),
  getByProduct: (productId) => api.get(`/api/addons/by-product/${productId}`),
  getById: (id) => api.get(`/api/addons/${id}`),
  create: (data) => api.post('/api/addons', data),
  update: (id, data) => api.put(`/api/addons/${id}`, data),
  toggleActive: (id) => api.patch(`/api/addons/${id}/toggle-active`),
  delete: (id) => api.delete(`/api/addons/${id}`),
  
  // Add-On Entries
  getEntries: (addOnId, params = {}) => api.get(`/api/addons/${addOnId}/entries`, { params }),
  createEntry: (addOnId, data) => api.post(`/api/addons/${addOnId}/entries`, data),
  updateEntry: (addOnId, entryId, data) => api.put(`/api/addons/${addOnId}/entries/${entryId}`, data),
  deleteEntry: (addOnId, entryId) => api.delete(`/api/addons/${addOnId}/entries/${entryId}`),
  reorderEntries: (addOnId, updates) => api.patch(`/api/addons/${addOnId}/entries/reorder`, { updates }),
  getAvailableEntries: (addOnId, data) => api.post(`/api/addons/${addOnId}/entries/available`, data),
  
  // Product Linking
  linkToProduct: (addOnId, productId, data = {}) => api.post(`/api/addons/${addOnId}/link-product/${productId}`, data),
  unlinkFromProduct: (addOnId, productId) => api.delete(`/api/addons/${addOnId}/unlink-product/${productId}`),
};

// Customers API
export const customersAPI = {
  getAll: (params = {}) => api.get('/api/customers', { params }),
  getById: (id) => api.get(`/api/customers/${id}`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/api/settings'),
  update: (data) => api.patch('/api/settings', data),
};

export default api;
